// @ts-strict-ignore
import React, { useMemo } from 'react'
import { snakeCase } from 'lodash'
import { Grid, List, ListItem, ListItemIcon, ListItemText, Accordion, AccordionSummary } from '@mui/material'
import MoreButtonDialog from 'components/MoreButtonDialog'
import FocusFadeGroup from 'components/FocusFadeGroup'
import { FormValue } from 'types'
import { Create, Work } from '@mui/icons-material'
import { useLazyLoadQuery } from 'react-relay'
import { graphql } from 'relay-runtime'
import { JobTypeFields_Query } from '__generated__/JobTypeFields_Query.graphql'
import { JobFormWrapper_Job$data } from '__generated__/JobFormWrapper_Job.graphql'
import { useEdges } from 'hooks/relay'
import FormErrorHint from '../../FormErrorHint'
import useStyles from './styles'

interface IJobTypeFieldsProps {
  industryId: FormValue<number>
  specializations: FormValue<any[]> // we just need to clear this field, we don't care what's in it
  jobSkills: FormValue<JobFormWrapper_Job$data['jobSkills']>
  editable?: boolean
  requiredExperienceYears: FormValue<number>
}

export default function JobTypeFields(props: IJobTypeFieldsProps) {
  const { classes } = useStyles()
  const {
    editable,
    industryId: { input: { value, onChange }, meta: { touched, error } },
    requiredExperienceYears,
    jobSkills,
    specializations,
  } = props

  // TODO: separate the editable portion of this component to avoid SSR not including the skills in the UI
  // TODO: change the form fields so that the freelancer type's name is included, so no query is needed to display this
  const data = useLazyLoadQuery<JobTypeFields_Query>(graphql`
    query JobTypeFields_Query {
      industries(first: 100) {
        edges {
          node {
            rawId
            name
          }
        }
      }
    }
  `, {}, {
    fetchPolicy: 'store-and-network',
  })

  const industries = useEdges(data?.industries?.edges)

  const selectedIndustry = industries?.find(industry => industry.rawId === value)
  const jobTypeLabel = useMemo(() => {
    const industry = industries.find(i => i.rawId === value)

    return industry?.name
  }, [industries, value])
  const requiredYears = requiredExperienceYears?.input?.value

  if (!selectedIndustry && !editable) {
    return null
  }

  const handleIndustryChange = (industryId: number) => {
    if (value !== industryId) {
      onChange(industryId)
      jobSkills.input.onChange([])
      specializations.input.onChange([])
    }
  }

  return (
    <Grid item xs={12} md={3}>
      <FocusFadeGroup>
        <List disablePadding dense>
          <ListItem className={classes.item}>
            <ListItemIcon className={classes.icon}>
              <Work />
            </ListItemIcon>

            <ListItemText
              className={classes.listItemText}
              primary={(
                <div className={classes.primaryText} title={jobTypeLabel}>
                  {jobTypeLabel || <span style={{ opacity: 0.6 }}>Select department...</span>}
                  {(jobTypeLabel && requiredYears > 0) && ` (${requiredYears}+ years)`}
                </div>
              )}
              secondary={(
                <React.Fragment>
                  Job department
                  <FormErrorHint message={touched && error} />
                </React.Fragment>
              )}
            />

            {editable && (
              <ListItemIcon className={classes.editButton}>
                <MoreButtonDialog
                  maxWidth="md"
                  tooltip="Select job department"
                  icon={<Create />}
                  dialogTitle="Select job department"
                  data-cy="job-industry-select"
                  CloseButtonProps={{
                    'data-cy': 'job-industry-close',
                  }}
                >
                  <div style={{ width: 800 }} />

                  {industries.map(industry => (
                    <Accordion
                      key={industry.rawId}
                      expanded={value === industry.rawId}
                      onClick={() => handleIndustryChange(industry.rawId)}
                      data-cy={`job-industry-select-${snakeCase(industry.name)}`}
                    >
                      <AccordionSummary>
                        {industry.name}
                      </AccordionSummary>
                    </Accordion>
                  ))}
                </MoreButtonDialog>
              </ListItemIcon>
            )}
          </ListItem>
        </List>
      </FocusFadeGroup>
    </Grid>
  )
}
